import React, { useState, useCallback } from "react";
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@material-ui/core";
import {
  GetApp as DownloadIcon,
  Image as ImageIcon,
  Code as SvgIcon,
  PictureAsPdf as PdfIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";

/**
 * FlowExport — Exportacao do canvas como PNG ou SVG.
 *
 * Usa html2canvas para capturar o viewport do React Flow,
 * e getNodesBounds + toSVG para export SVG nativo.
 */
export default function FlowExport({ flowName, getViewport, reactFlowWrapper }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleOpen = useCallback((e) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // ─── Export PNG via html2canvas ───
  const handleExportPNG = useCallback(async () => {
    handleClose();
    if (!reactFlowWrapper?.current) {
      toast.error("Canvas nao encontrado");
      return;
    }

    setExporting(true);
    try {
      // Buscar o elemento .react-flow dentro do wrapper
      const flowElement = reactFlowWrapper.current.querySelector(".react-flow");
      if (!flowElement) {
        toast.error("Elemento do flow nao encontrado");
        return;
      }

      const canvas = await html2canvas(flowElement, {
        backgroundColor: "#ffffff",
        scale: 2, // Alta resolucao
        useCORS: true,
        logging: false,
        // Ignorar minimap e controls para export limpo
        ignoreElements: (element) => {
          const cls = element.className || "";
          if (typeof cls === "string") {
            return cls.includes("react-flow__minimap") ||
                   cls.includes("react-flow__controls") ||
                   cls.includes("react-flow__panel");
          }
          return false;
        },
      });

      // Converter para blob e download
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Erro ao gerar imagem");
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${flowName || "fluxo"}_export.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("PNG exportado com sucesso!");
      }, "image/png");
    } catch (err) {
      console.error("[FlowExport] Erro PNG:", err);
      toast.error("Erro ao exportar PNG");
    } finally {
      setExporting(false);
    }
  }, [reactFlowWrapper, flowName, handleClose]);

  // ─── Export SVG (extrai o SVG interno do React Flow) ───
  const handleExportSVG = useCallback(async () => {
    handleClose();
    if (!reactFlowWrapper?.current) {
      toast.error("Canvas nao encontrado");
      return;
    }

    setExporting(true);
    try {
      const flowElement = reactFlowWrapper.current.querySelector(".react-flow");
      if (!flowElement) {
        toast.error("Elemento do flow nao encontrado");
        return;
      }

      // Capturar como PNG e converter para SVG container
      const canvas = await html2canvas(flowElement, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) => {
          const cls = element.className || "";
          if (typeof cls === "string") {
            return cls.includes("react-flow__minimap") ||
                   cls.includes("react-flow__controls") ||
                   cls.includes("react-flow__panel");
          }
          return false;
        },
      });

      const dataUrl = canvas.toDataURL("image/png");
      const width = canvas.width / 2;
      const height = canvas.height / 2;

      // Criar SVG wrapper com imagem embutida
      const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="white"/>
  <image href="${dataUrl}" width="${width}" height="${height}"/>
</svg>`;

      const blob = new Blob([svgContent], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${flowName || "fluxo"}_export.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("SVG exportado com sucesso!");
    } catch (err) {
      console.error("[FlowExport] Erro SVG:", err);
      toast.error("Erro ao exportar SVG");
    } finally {
      setExporting(false);
    }
  }, [reactFlowWrapper, flowName, handleClose]);

  return (
    <>
      <Tooltip title="Exportar fluxo">
        <IconButton
          size="small"
          onClick={handleOpen}
          style={{ padding: 6 }}
          disabled={exporting}
        >
          {exporting ? (
            <CircularProgress size={18} />
          ) : (
            <DownloadIcon fontSize="small" style={{ color: "#555" }} />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          style: {
            borderRadius: 8,
            minWidth: 180,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          },
        }}
      >
        <MenuItem onClick={handleExportPNG} style={{ borderRadius: 6, margin: "2px 4px" }}>
          <ListItemIcon style={{ minWidth: 36 }}>
            <ImageIcon fontSize="small" style={{ color: "#22c55e" }} />
          </ListItemIcon>
          <ListItemText
            primary="Exportar PNG"
            secondary="Imagem de alta resolucao"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600 } }}
            secondaryTypographyProps={{ style: { fontSize: 11 } }}
          />
        </MenuItem>

        <MenuItem onClick={handleExportSVG} style={{ borderRadius: 6, margin: "2px 4px" }}>
          <ListItemIcon style={{ minWidth: 36 }}>
            <SvgIcon fontSize="small" style={{ color: "#3b82f6" }} />
          </ListItemIcon>
          <ListItemText
            primary="Exportar SVG"
            secondary="Vetorial escalavel"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600 } }}
            secondaryTypographyProps={{ style: { fontSize: 11 } }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
